// frontend/src/components/BookReading/BookReadingLayout.js
//#region Imports
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { AuthContext } from '../../contexts/AuthContext';
import { LanguageContext } from '../../contexts/LanguageProvider';
import { useAlertContext } from '../../contexts/AlertContext';
import axios from 'axios';
import { API_ENDPOINTS } from '../../configs/apiConfig';
import translations from './translations';
//#endregion

//#region Contexto
export const BookReadingContext = createContext();
//#endregion

//#region Componente Principal
function BookReading() {
  //#region Declarações
  const { isLoggedIn } = useContext(AuthContext);
  const { language } = useContext(LanguageContext);
  const location = useLocation();
  const navigate = useNavigate();
  
  const [books, setBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [suggestionTypes, setSuggestionTypes] = useState([]);
  const [loadingSuggestionTypes, setLoadingSuggestionTypes] = useState(false);

  const { showError } = useAlertContext();

  const t = translations[language] || translations.pt;
  //#endregion

  //#region Funções Auxiliares - Determinar o título baseado na rota atual
  const getPageTitle = () => {
    if (location.pathname.includes('/chapter/')) {
      return selectedChapter?.title || t.reading;
    } else if (location.pathname.includes('/book/')) {
      return selectedBook?.title || t.reading;
    } else {
      return t.reading;
    }
  };

  // Função para navegar de volta aos livros
  const resetToBooks = () => {
    setSelectedBook(null);
    setSelectedChapter(null);
    navigate('.');
  };

  // Função para navegar de volta aos capítulos
  const backToChapters = () => {
    if (selectedBook) {
      setSelectedChapter(null);
      navigate(`./book/${selectedBook.id}`);
    }
  };
  //#endregion

  //#region Efeitos (useEffect) Carregar livros e tipos de sugestão
  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const auth = localStorage.getItem('auth');
        const response = await axios.get(API_ENDPOINTS.BOOKS_REFERENCES, {
          headers: { Authorization: `Basic ${auth}` },
        });
        const booksWithChapters = response.data.map(book => ({
          ...book,
          chapters: book.chapters || []
        }));
        setBooks(booksWithChapters);
      } catch (error) {
        showError(t.failBooks + error.message);
      }
    };

     const fetchSuggestionTypes = async () => {
      setLoadingSuggestionTypes(true);
      try {
        const auth = localStorage.getItem('auth');
        const response = await axios.get(API_ENDPOINTS.CHAPTERS_SUGESTION_TYPE, {
          headers: { Authorization: `Basic ${auth}` },
          // O parâmetro 'lang' é enviado para o backend
          params: { lang: language } 
        });
        setSuggestionTypes(response.data || []);
      } catch (error) {
        console.error('Erro ao buscar tipos de sugestão:', error);
        showError(t.failSuggestionTypes + (error.message || 'Erro desconhecido'));
        setSuggestionTypes([]);
      } finally {
        setLoadingSuggestionTypes(false);
      }
    };

    if (isLoggedIn) {
      fetchBooks();
      fetchSuggestionTypes();
    }
  }, [isLoggedIn,language, t.failBooks, t.failSuggestionTypes, showError]);
  //#endregion

  //#region Contexto
  // Context value para compartilhar com componentes filhos
  const contextValue = {
    books,
    selectedBook,
    setSelectedBook,
    selectedChapter,
    setSelectedChapter,
    suggestionTypes,
    loadingSuggestionTypes,
    translations: t,
    resetToBooks,
    backToChapters
  };
  //#endregion

  //#region Renderização
  // Lógica de controle de visibilidade baseada na profundidade da rota
  // Se a localização atual for diferente do caminho base, estamos visualizando um conteúdo
  const isAtRoot = location.pathname.endsWith('/read') || location.pathname.endsWith('/read/');
  const isViewingContent = !isAtRoot;
 // console.log('Current path:', location.pathname, 'isAtRoot:', isAtRoot, 'isViewingContent:', isViewingContent);

  return (
    <BookReadingContext.Provider value={contextValue}>
      <div className="book-reading">
        <div className="page-header">
          <h1 className="page-title">{getPageTitle()}</h1>
          {isViewingContent && (
            <Button variant="outline-secondary" size="sm" onClick={resetToBooks}>
              {t.backToBooks}
            </Button>
          )}
        </div>

        <Outlet />
      </div>
    </BookReadingContext.Provider>
  );
  //#endregion
}

export default BookReading;
